import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Stack,
  Box,
  ToggleButtonGroup,
  ToggleButton,
  InputAdornment,
  Tooltip,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Lease, FeedEvent } from '@urbanmix-tech/shared-js';
import { Concession } from '@/types/lease';
import { saveFeedEvent, saveLease } from '@/data';
import { useAuth } from '@/shared/context/AuthProvider';
import { useAlert } from '@/contexts/Alert/AlertContext';
import dayjs from 'dayjs';

export interface LeaseEditDialogProps {
  open: boolean;
  onClose: () => void;
  lease: Lease | null;
  selectedUnit: any;
  onLeaseUpdated?: (lease: Lease) => void;
}

interface LeaseChange {
  field: string;
  oldValue: any;
  newValue: any;
}

type ConcessionMode = 'manual' | 'effective' | 'custom';

// Represents a month in the lease that can have a concession
interface LeaseMonth {
  month: number; // 1-12
  year: number;
  label: string; // e.g., "Jun 2026"
  concessionAmount: number; // 0-1 (multiplier of monthly rent)
}

/**
 * Convert a date value (Date, string, or pseudo date) to a Date object
 */
function toDate(value: unknown): Date | null {
  if (!value) return null;

  // Already a valid Date
  if (value instanceof Date && !isNaN(value.getTime())) {
    return value;
  }

  // Handle pseudo date (Firestore Timestamp-like object with seconds)
  if (typeof value === 'object' && value !== null && 'seconds' in value) {
    return new Date((value as { seconds: number }).seconds * 1000);
  }

  // Handle Firestore Timestamp with toDate method
  if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as { toDate: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }

  // Try parsing as string
  if (typeof value === 'string') {
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      return date;
    }
  }

  return null;
}

/**
 * Calculate lease length in months with rounding for dates within ~3 days of full months
 */
function calculateLeaseMonths(startDateVal: unknown, endDateVal: unknown): number {
  const startDate = toDate(startDateVal);
  const endDate = toDate(endDateVal);

  if (!startDate || !endDate) return 0;

  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const exactMonths = end.diff(start, 'month', true);

  // Round to nearest month if within ~3 days (0.1 of a month ≈ 3 days)
  const roundedMonths = Math.round(exactMonths);
  const difference = Math.abs(exactMonths - roundedMonths);

  if (difference <= 0.1) {
    return roundedMonths;
  }

  return exactMonths;
}

/**
 * Generate concession entries starting from the last month of the lease, going backwards
 */
function generateConcessionEntries(
  _startDate: Date,
  endDate: Date,
  _grossRent: number,
  monthsOfConcession: number
): Concession[] {
  const end = dayjs(endDate);
  const concessions: Concession[] = [];

  // Apply concessions starting from the last month, going backwards
  const fullMonths = Math.floor(monthsOfConcession);
  const partialMonth = monthsOfConcession - fullMonths;

  // Start from the last month and go backwards
  let currentDate = end;
  for (let i = 0; i < fullMonths; i++) {
    concessions.push({
      month: currentDate.month() + 1,
      year: currentDate.year(),
      type: 'rentMultiplier',
      amount: 1, // 1 = one full month of rent
      description: `Concession: 1 month free rent`,
    });
    currentDate = currentDate.subtract(1, 'month');
  }

  // Add partial month if there is one
  if (partialMonth > 0.01) {
    concessions.push({
      month: currentDate.month() + 1,
      year: currentDate.year(),
      type: 'rentMultiplier',
      amount: Math.round(partialMonth * 10000) / 10000,
      description: `Concession: ${partialMonth.toFixed(2)} months free rent`,
    });
  }

  return concessions;
}

export function LeaseEditDialog({
  open,
  onClose,
  lease,
  selectedUnit,
  onLeaseUpdated,
}: LeaseEditDialogProps) {
  const [editedLease, setEditedLease] = useState<Lease | null>(lease);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [changes, setChanges] = useState<LeaseChange[]>([]);

  // Concession editing state
  const [concessionMode, setConcessionMode] = useState<ConcessionMode>('manual');
  const [monthsOfConcession, setMonthsOfConcession] = useState<number>(0);
  const [customConcessions, setCustomConcessions] = useState<LeaseMonth[]>([]);
  const [effectiveRent, setEffectiveRent] = useState<number>(0);

  const { userData, user } = useAuth();
  const { showAlert } = useAlert();

  // Calculate lease length
  const leaseMonths = useMemo(() => {
    if (!editedLease) return 0;
    return calculateLeaseMonths(editedLease.leaseStartDate, editedLease.leaseEndDate);
  }, [editedLease?.leaseStartDate, editedLease?.leaseEndDate]);

  // Calculate derived values based on concession mode
  const concessionCalculations = useMemo(() => {
    if (!editedLease) return null;
    const grossRent = editedLease.monthlyRent;

    if (concessionMode === 'manual') {
      // Manual mode: user enters months of concession
      const totalConcessionAmount = Math.round(monthsOfConcession * grossRent * 100) / 100;
      const calculatedEffectiveRent = leaseMonths > 0
        ? Math.round((grossRent - (totalConcessionAmount / leaseMonths)) * 100) / 100
        : grossRent;

      return {
        totalConcessionAmount,
        effectiveRent: calculatedEffectiveRent,
        monthsOfConcession,
      };
    } else if (concessionMode === 'effective') {
      // Effective rent mode: user enters effective rent, calculate concession
      if (effectiveRent >= grossRent || effectiveRent <= 0) {
        return {
          totalConcessionAmount: 0,
          effectiveRent: grossRent,
          monthsOfConcession: 0,
        };
      }

      const monthlyDifference = grossRent - effectiveRent;
      const totalConcessionAmount = Math.round(monthlyDifference * leaseMonths * 100) / 100;
      const calculatedMonths = Math.round((totalConcessionAmount / grossRent) * 100) / 100;

      return {
        totalConcessionAmount,
        effectiveRent,
        monthsOfConcession: calculatedMonths,
      };
    } else {
      // Custom mode: sum up custom concessions
      const totalMonths = customConcessions.reduce((sum, m) => sum + m.concessionAmount, 0);
      const totalConcessionAmount = Math.round(totalMonths * grossRent * 100) / 100;
      const calculatedEffectiveRent = leaseMonths > 0
        ? Math.round((grossRent - (totalConcessionAmount / leaseMonths)) * 100) / 100
        : grossRent;

      return {
        totalConcessionAmount,
        effectiveRent: calculatedEffectiveRent,
        monthsOfConcession: totalMonths,
      };
    }
  }, [editedLease?.monthlyRent, concessionMode, monthsOfConcession, effectiveRent, leaseMonths, customConcessions]);

  // Reset state when dialog opens
  useEffect(() => {
    if (open && lease) {
      setEditedLease(lease);
      setReason('');
      setChanges([]);
      setConcessionMode('manual');

      // Initialize concession values from existing lease
      const existingMonths = lease.monthlyRent > 0
        ? (lease.totalConcessionAmount || 0) / lease.monthlyRent
        : 0;
      setMonthsOfConcession(Math.round(existingMonths * 100) / 100);

      // Calculate existing effective rent
      const months = calculateLeaseMonths(lease.leaseStartDate, lease.leaseEndDate);
      if (months > 0) {
        const existingEffective = lease.monthlyRent - ((lease.totalConcessionAmount || 0) / months);
        setEffectiveRent(Math.round(existingEffective * 100) / 100);
      } else {
        setEffectiveRent(lease.monthlyRent);
      }

      // Initialize custom concessions from existing lease concessions array
      const startDate = toDate(lease.leaseStartDate);
      const endDate = toDate(lease.leaseEndDate);
      if (startDate && endDate) {
        const leaseMonthsList: LeaseMonth[] = [];
        let currentDate = dayjs(startDate);
        const end = dayjs(endDate);

        while (currentDate.isBefore(end) || currentDate.isSame(end, 'month')) {
          const month = currentDate.month() + 1;
          const year = currentDate.year();
          // Find existing concession for this month
          const existingConcession = Array.isArray(lease.concessions)
            ? lease.concessions.find((c) => c.month === month && c.year === year)
            : undefined;

          leaseMonthsList.push({
            month,
            year,
            label: currentDate.format('MMM YYYY'),
            concessionAmount: existingConcession?.amount || 0,
          });
          currentDate = currentDate.add(1, 'month');
        }
        setCustomConcessions(leaseMonthsList);
      }
    }
  }, [open, lease]);

  const handleFieldChange = (field: keyof Lease, value: any) => {
    if (!editedLease) return;

    const newLease = { ...editedLease, [field]: value };
    setEditedLease(newLease);

    // Track change
    const oldValue = lease?.[field];
    if (oldValue !== value) {
      const existingChangeIndex = changes.findIndex((c) => c.field === field);
      if (existingChangeIndex >= 0) {
        const updatedChanges = [...changes];
        updatedChanges[existingChangeIndex] = { field, oldValue, newValue: value };
        setChanges(updatedChanges);
      } else {
        setChanges([...changes, { field, oldValue, newValue: value }]);
      }
    }
  };

  // Check if concession has changed
  const concessionHasChanged = useMemo(() => {
    if (!lease || !concessionCalculations) return false;
    const originalTotal = lease.totalConcessionAmount || 0;
    const newTotal = concessionCalculations.totalConcessionAmount;
    return Math.abs(originalTotal - newTotal) > 0.01;
  }, [lease, concessionCalculations]);

  // Get all changes including concession
  const allChanges = useMemo(() => {
    const result = [...changes];
    if (concessionHasChanged && concessionCalculations && lease) {
      const existingIndex = result.findIndex((c) => c.field === 'concessions');
      const concessionChange = {
        field: 'concessions',
        oldValue: `$${(lease.totalConcessionAmount || 0).toLocaleString()} (${((lease.totalConcessionAmount || 0) / (lease.monthlyRent || 1)).toFixed(2)} months)`,
        newValue: `$${concessionCalculations.totalConcessionAmount.toLocaleString()} (${concessionCalculations.monthsOfConcession.toFixed(2)} months)`,
      };
      if (existingIndex >= 0) {
        result[existingIndex] = concessionChange;
      } else {
        result.push(concessionChange);
      }
    }
    return result;
  }, [changes, concessionHasChanged, concessionCalculations, lease]);

  const handleSave = async () => {
    if (!editedLease || !lease || !selectedUnit || !user || !userData) {
      showAlert('Missing required information', 'error');
      return;
    }

    if (!reason.trim()) {
      showAlert('Please provide a reason for the change', 'warning');
      return;
    }

    if (allChanges.length === 0) {
      showAlert('No changes detected', 'info');
      return;
    }

    setSubmitting(true);
    try {
      // Prepare lease with updated concession data
      let leaseToSave = { ...editedLease };

      if (concessionHasChanged && concessionCalculations) {
        const startDate = toDate(editedLease.leaseStartDate);
        const endDate = toDate(editedLease.leaseEndDate);

        if (startDate && endDate) {
          let concessionEntries: Concession[];

          if (concessionMode === 'custom') {
            // Use custom concessions directly
            concessionEntries = customConcessions
              .filter((m) => m.concessionAmount > 0)
              .map((m) => ({
                month: m.month,
                year: m.year,
                type: 'rentMultiplier' as const,
                amount: m.concessionAmount,
                description: m.concessionAmount >= 1
                  ? 'Concession: 1 month free rent'
                  : `Concession: ${m.concessionAmount.toFixed(2)} months free rent`,
              }));
          } else {
            // Generate concession entries from months (applied from end backwards)
            concessionEntries = generateConcessionEntries(
              startDate,
              endDate,
              editedLease.monthlyRent,
              concessionCalculations.monthsOfConcession
            );
          }

          leaseToSave = {
            ...leaseToSave,
            concessions: concessionEntries as any, // TODO: remove cast after shared-js update
            totalConcessionAmount: concessionCalculations.totalConcessionAmount,
          };
        }
      }

      // Save the updated lease to the database
      await saveLease(leaseToSave);

      // Create feed event to record the lease change
      const changeEvent: FeedEvent = {
        id: `lease_change_${leaseToSave.id}_${Date.now()}`,
        eventType: 'lease_change',
        eventDate: new Date(),
        projectId: leaseToSave.projectId,
        buildingId: leaseToSave.buildingId,
        unitId: leaseToSave.unitId,
        userId: user.uid,
        eventData: {
          leaseId: leaseToSave.id,
          unitId: leaseToSave.unitId,
          unitName: selectedUnit.name || selectedUnit.id,
          reason: reason.trim(),
          changes: allChanges.map((change) => ({
            field: change.field,
            oldValue: change.oldValue,
            newValue: change.newValue,
          })),
          changedBy: userData.fullName || userData.email,
          changedById: user.uid,
          timestamp: new Date().toISOString(),
        },
      };

      // Save the feed event
      await saveFeedEvent(changeEvent);

      showAlert('Lease updated and change recorded', 'success');
      onLeaseUpdated?.(leaseToSave);
      handleClose();
    } catch (error) {
      console.error('Error saving lease change:', error);
      showAlert('Failed to save lease change', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setEditedLease(null);
    setReason('');
    setChanges([]);
    onClose();
  };

  const getFormattedDate = (date: unknown): string => {
    // If already a YYYY-MM-DD string, return directly to avoid timezone conversion
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date;
    }
    const realDate = toDate(date);
    if (realDate) {
      return dayjs(realDate).format('YYYY-MM-DD');
    }
    return '';
  };

  if (!lease || !editedLease) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Edit Lease</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Unit: {selectedUnit?.name || selectedUnit?.id}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Lease ID: {lease.id}
            </Typography>
          </Box>

          <TextField
            label="Monthly Rent ($)"
            type="number"
            value={editedLease.monthlyRent || ''}
            onChange={(e) => handleFieldChange('monthlyRent', parseFloat(e.target.value) || 0)}
            fullWidth
            inputProps={{ step: '0.01' }}
          />

          <TextField
            label="Lease Start Date"
            type="date"
            value={getFormattedDate(editedLease.leaseStartDate)}
            onChange={(e) =>
              handleFieldChange('leaseStartDate', e.target.value)
            }
            fullWidth
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="Lease End Date"
            type="date"
            value={getFormattedDate(editedLease.leaseEndDate)}
            onChange={(e) =>
              handleFieldChange('leaseEndDate', e.target.value)
            }
            fullWidth
            InputLabelProps={{ shrink: true }}
          />

          <FormControl fullWidth>
            <InputLabel id="lease-status-label">Lease Status</InputLabel>
            <Select
              labelId="lease-status-label"
              value={(editedLease as any).status || 'active'}
              label="Lease Status"
              onChange={(e) => {
                const newStatus = e.target.value === 'active' ? undefined : e.target.value;
                handleFieldChange('status', newStatus);
              }}
            >
              <MenuItem value="active">Active (determined by dates)</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="canceled">Canceled</MenuItem>
            </Select>
          </FormControl>

          {/* Concession Editing Section */}
          <Box sx={{ bgcolor: 'rgba(39, 39, 42, 0.3)', p: 2, borderRadius: 1, border: '1px solid rgba(255,255,255,0.1)' }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
              Concessions
            </Typography>

            <ToggleButtonGroup
              value={concessionMode}
              exclusive
              onChange={(_, newMode) => newMode && setConcessionMode(newMode)}
              size="small"
              sx={{ mb: 2 }}
            >
              <ToggleButton value="manual">
                <Tooltip title="Enter months of free rent equivalent">
                  <span>Months</span>
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="effective">
                <Tooltip title="Enter effective rent and auto-calculate concession">
                  <span>Effective Rent</span>
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="custom">
                <Tooltip title="Select specific months for concessions">
                  <span>Custom</span>
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>

            {concessionMode === 'manual' && (
              <TextField
                label="Months of Concession"
                type="number"
                value={monthsOfConcession || ''}
                onChange={(e) => setMonthsOfConcession(parseFloat(e.target.value) || 0)}
                fullWidth
                inputProps={{ step: '0.1', min: 0 }}
                helperText={`Equivalent to $${((monthsOfConcession || 0) * (editedLease.monthlyRent || 0)).toLocaleString()} total`}
                InputProps={{
                  endAdornment: <InputAdornment position="end">months</InputAdornment>,
                }}
              />
            )}

            {concessionMode === 'effective' && (
              <TextField
                label="Effective Monthly Rent"
                type="number"
                value={effectiveRent || ''}
                onChange={(e) => setEffectiveRent(parseFloat(e.target.value) || 0)}
                fullWidth
                inputProps={{ step: '0.01', min: 0, max: editedLease.monthlyRent }}
                helperText={effectiveRent > 0 && effectiveRent < editedLease.monthlyRent
                  ? `Concession: $${(editedLease.monthlyRent - effectiveRent).toLocaleString()}/mo × ${leaseMonths} months`
                  : 'Enter an amount less than gross rent to calculate concession'}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            )}

            {concessionMode === 'custom' && (
              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                  Click on months to toggle full concession, or enter a custom amount (0-1)
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, maxHeight: 200, overflowY: 'auto' }}>
                  {customConcessions.map((leaseMonth, index) => (
                    <Tooltip
                      key={`${leaseMonth.year}-${leaseMonth.month}`}
                      title={leaseMonth.concessionAmount > 0
                        ? `$${(leaseMonth.concessionAmount * editedLease.monthlyRent).toLocaleString()} concession`
                        : 'Click to add 1 month concession'}
                    >
                      <Chip
                        label={leaseMonth.label}
                        size="small"
                        onClick={() => {
                          const updated = [...customConcessions];
                          // Toggle between 0 and 1
                          updated[index] = {
                            ...updated[index],
                            concessionAmount: updated[index].concessionAmount > 0 ? 0 : 1,
                          };
                          setCustomConcessions(updated);
                        }}
                        onDelete={leaseMonth.concessionAmount > 0 ? () => {
                          const updated = [...customConcessions];
                          updated[index] = { ...updated[index], concessionAmount: 0 };
                          setCustomConcessions(updated);
                        } : undefined}
                        sx={{
                          bgcolor: leaseMonth.concessionAmount > 0
                            ? leaseMonth.concessionAmount >= 1
                              ? 'success.main'
                              : 'warning.main'
                            : 'rgba(255,255,255,0.1)',
                          color: leaseMonth.concessionAmount > 0 ? 'white' : 'text.secondary',
                          '&:hover': {
                            bgcolor: leaseMonth.concessionAmount > 0 ? 'success.dark' : 'rgba(255,255,255,0.2)',
                          },
                        }}
                      />
                    </Tooltip>
                  ))}
                </Box>
                {customConcessions.some((m) => m.concessionAmount > 0) && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'success.main' }}>
                    Selected: {customConcessions.filter((m) => m.concessionAmount > 0).map((m) => m.label).join(', ')}
                  </Typography>
                )}
              </Box>
            )}

            {/* Summary of calculated concession */}
            {concessionCalculations && concessionCalculations.totalConcessionAmount > 0 && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: 'rgba(76, 175, 80, 0.1)', borderRadius: 1, border: '1px solid rgba(76, 175, 80, 0.3)' }}>
                <Typography variant="caption" sx={{ display: 'block', color: 'success.main', fontWeight: 600 }}>
                  Calculated Concession
                </Typography>
                <Stack direction="row" spacing={3} sx={{ mt: 0.5 }}>
                  <Typography variant="body2">
                    <strong>Total:</strong> ${concessionCalculations.totalConcessionAmount.toLocaleString()}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Months:</strong> {concessionCalculations.monthsOfConcession.toFixed(2)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Effective:</strong> ${concessionCalculations.effectiveRent.toLocaleString()}/mo
                  </Typography>
                </Stack>
              </Box>
            )}

            {/* Show lease term info */}
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
              Lease term: {leaseMonths} months
            </Typography>
          </Box>

          <Box sx={{ bgcolor: 'rgba(39, 39, 42, 0.5)', p: 2, borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Changes Summary
            </Typography>
            {allChanges.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                No changes detected yet
              </Typography>
            ) : (
              <Stack spacing={1}>
                {allChanges.map((change) => (
                  <Typography key={change.field} variant="body2">
                    <strong>{change.field}:</strong> {String(change.oldValue)} → {String(change.newValue)}
                  </Typography>
                ))}
              </Stack>
            )}
          </Box>

          <TextField
            label="Reason for Change *"
            placeholder="Explain why this lease is being modified..."
            multiline
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            variant="outlined"
            helperText="This will be recorded in the lease history"
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(39, 39, 42, 0.5)',
              },
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={submitting || allChanges.length === 0 || !reason.trim()}
        >
          {submitting ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
